import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button, Typography } from '@mui/material';
import { ButtonGroupWrapper } from '@sharedCR/coreDesignSystem/Buttons';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { COMPOSED_CLAIMS } from 'constants/composedClaims';
import { HORIZONTAL_ALIGNMENT } from 'constants/ui';

import { selectIsClaimAllowed } from 'store/auth/selectors';
import { closeConfirmationDialogAction, closeDialogAction } from 'store/global/actions';

import { NoLoanProgramsPopupTestIds } from './NoLoanProgramsPopup.testIds';

export const NoLoanProgramsPopupMessage = {
  NO_PROGRAMS: 'There are no programs that fit the loan criteria.',
  CHANGES_CANNOT_BE_SAVED: 'The proposed changes cannot be saved.',
  PROGRAMS_NOT_SHOWN: 'Loan Program is invalid. Please go to Loan tab and change program.',
};

const NoLoanProgramsPopup = ({
  isFromSelectProgramRate = false,
  message = NoLoanProgramsPopupMessage.NO_PROGRAMS,
  onCancel = _.noop,
  isSaveWithoutProgramHidden = false,
  isSaveWithoutProgramNeededOnNoPrograms = false,
  onAccept = _.noop,
  setIsLoading,
  isLoading,
  isRestyled,
}) => {
  const dispatch = useDispatch();

  const isSaveWithoutProgramAllowed = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.LOAN_APPLICATION_SAVE_WITHOUT_PROGRAM),
  );

  const isSaveWithoutProgramButtonVisible =
    isSaveWithoutProgramAllowed &&
    isSaveWithoutProgramNeededOnNoPrograms &&
    !isSaveWithoutProgramHidden;

  const formattedMessage = useMemo(() => {
    if (message === NoLoanProgramsPopupMessage.NO_PROGRAMS) {
      return isSaveWithoutProgramButtonVisible || isFromSelectProgramRate
        ? NoLoanProgramsPopupMessage.NO_PROGRAMS
        : [
            NoLoanProgramsPopupMessage.NO_PROGRAMS,
            NoLoanProgramsPopupMessage.CHANGES_CANNOT_BE_SAVED,
          ];
    }

    return message;
  }, [message, isSaveWithoutProgramButtonVisible, isFromSelectProgramRate]);

  const onCancelClick = useCallback(() => {
    dispatch(isRestyled ? closeConfirmationDialogAction() : closeDialogAction());

    onCancel();
  }, [dispatch, isRestyled, onCancel]);

  const onSaveWithoutProgramClick = useCallback(() => {
    setIsLoading(true);

    onAccept({ saveWithoutProgram: true })
      .then(() => dispatch(isRestyled ? closeConfirmationDialogAction() : closeDialogAction()))
      .finally(() => setIsLoading(false));
  }, [dispatch, isRestyled, onAccept, setIsLoading]);

  if (isRestyled) {
    return (
      <Box className="flex flex-col items-center gap-24 w-full">
        <Typography className="text-center" component="h5" variant="bold16">
          {formattedMessage}
        </Typography>

        <ButtonGroupWrapper alignment={HORIZONTAL_ALIGNMENT.CENTER} className="w-full">
          {isSaveWithoutProgramButtonVisible && (
            <Button
              className="flex-1"
              data-testid={NoLoanProgramsPopupTestIds.SAVE_WITHOUT_PROGRAM_BUTTON}
              disabled={isLoading}
              size="large"
              variant="line"
              onClick={onSaveWithoutProgramClick}
            >
              Save Without Program
            </Button>
          )}

          <Button
            className="flex-1"
            data-testid={NoLoanProgramsPopupTestIds.CANCEL_BUTTON}
            disabled={isLoading}
            size="large"
            onClick={onCancelClick}
          >
            Cancel
          </Button>
        </ButtonGroupWrapper>
      </Box>
    );
  }

  //TODO: remove after redesign
  return (
    <div className="flex flex-col justify-center min-w-400 p-24">
      <Typography className="mb-12 whitespace-pre-line" variant="bold16">
        {formattedMessage}
      </Typography>

      <div className="flex justify-end">
        {isSaveWithoutProgramButtonVisible && (
          <Button
            className="mr-5"
            color="primary"
            data-testid={NoLoanProgramsPopupTestIds.SAVE_WITHOUT_PROGRAM_BUTTON}
            disabled={isLoading}
            type="button"
            variant="contained"
            onClick={onSaveWithoutProgramClick}
          >
            Save without a program/rate
          </Button>
        )}

        <Button
          color="primary"
          data-testid={NoLoanProgramsPopupTestIds.CANCEL_BUTTON}
          disabled={isLoading}
          type="button"
          variant="contained"
          onClick={onCancelClick}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

NoLoanProgramsPopup.propTypes = {
  ...WithLoadingPropTypes,
  isSaveWithoutProgramHidden: PropTypes.bool,
  isFromSelectProgramRate: PropTypes.bool,
  isSaveWithoutProgramNeededOnNoPrograms: PropTypes.bool,
  message: PropTypes.string,
  onAccept: PropTypes.func,
  onCancel: PropTypes.func,
};

export default withLoading(NoLoanProgramsPopup);
